/**
 * HeightSafetyQuoteRequestPreview · iter303ak-fix26 — Quote Sent · T25 · A40
 * ─────────────────────────────────────────────────────────────────────────
 * Accordion — one PDF preview per active scaffold_supplier for the
 * `height_safety_quote_request` letter. Per-supplier "Include in send"
 * toggle (default on). Writes `height_safety_supplier_selection` map.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FileText, ExternalLink, CheckCircle2 } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HeightSafetyQuoteRequestPreview({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const letterKey = params.letter_key || 'height_safety_quote_request';

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState(() => value?.selection || {});
  const [expanded, setExpanded] = useState(new Set());
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/scaffold-suppliers');
        setSuppliers(Array.isArray(data) ? data : []);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Failed to load suppliers');
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    // Default all active suppliers to "include" on first mount
    setSelection(prev => {
      if (Object.keys(prev).length > 0) return prev;
      const next = {};
      for (const s of suppliers) {
        const sid = s._id || s.id;
        next[sid] = { include: true, name: s.name || s.company_name || s.contact_name || 'Supplier', email: s.email || '' };
      }
      return next;
    });
  }, [suppliers]);

  const toggleInclude = (sid) => {
    setSelection(prev => ({
      ...prev,
      [sid]: { ...(prev[sid] || {}), include: !(prev[sid] || {}).include },
    }));
  };

  const toggleExpand = (sid) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };

  useEffect(() => {
    if (onChange) onChange({ selection });
  }, [selection, onChange]);

  const includedCount = useMemo(
    () => Object.values(selection).filter(v => v.include).length,
    [selection]
  );

  const pdfHref = (sid) => `${BACKEND_URL}/api/reports/status-letter/${letterKey}/${appraisalId}?_supplier_id=${encodeURIComponent(sid)}`;

  return (
    <div className="space-y-4" data-testid="wf-hs-quote-request-preview">
      <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 mt-0.5 shrink-0 text-blue-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-blue-900">Preview Quote Request Letters</div>
            <div className="text-sm mt-1 text-blue-800">
              {params.prompt || 'One letter preview per active scaffold supplier. Uncheck any supplier you don\'t want to send the request to.'}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-blue-600 font-semibold">Selected</div>
            <div className="text-lg font-bold text-blue-900 leading-none">{includedCount}<span className="text-xs text-blue-500 font-normal"> / {suppliers.length}</span></div>
          </div>
        </div>
      </div>

      {loading && <div className="p-6 text-sm text-slate-400 italic">Loading suppliers…</div>}

      {!loading && suppliers.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No active scaffold suppliers found in the DB. Add at least one in Settings → Scaffold Suppliers before continuing.
        </div>
      )}

      {suppliers.map((s) => {
        const sid = s._id || s.id;
        const sel = selection[sid] || { include: true };
        const isExpanded = expanded.has(sid);
        return (
          <div key={sid} className={`border rounded-lg ${sel.include ? 'border-blue-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-70'}`} data-testid={`wf-hs-preview-row-${sid}`}>
            <div className="flex items-center justify-between gap-3 p-3">
              <button
                type="button"
                onClick={() => toggleExpand(sid)}
                className="flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-slate-900 min-w-0 flex-1 text-left"
              >
                <span className={`inline-block transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                <span className="truncate">{sel.name || s.name || s.company_name || 'Supplier'}</span>
                {s.email && <span className="text-[10px] text-slate-500">{s.email}</span>}
              </button>
              <div className="flex items-center gap-3 shrink-0">
                <a href={pdfHref(sid)} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
                  <ExternalLink size={11} /> New tab
                </a>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    checked={!!sel.include}
                    onChange={() => toggleInclude(sid)}
                    className="rounded"
                    data-testid={`wf-hs-preview-include-${sid}`}
                  />
                  <span className={`font-medium ${sel.include ? 'text-emerald-700' : 'text-slate-500'}`}>Include in send</span>
                </label>
              </div>
            </div>
            {isExpanded && (
              <div className="border-t border-slate-100 p-2 bg-slate-50">
                <iframe src={pdfHref(sid)} title={sel.name || 'Quote Request'} className="w-full bg-white border border-slate-200 rounded" style={{ height: '600px' }} />
              </div>
            )}
          </div>
        );
      })}

      {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-hs-preview-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-hs-preview-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={includedCount === 0}
            className="px-4 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-hs-preview-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue to Send ({includedCount})
          </button>
        </div>
      </div>
    </div>
  );
}
